<div class="container-fluid">
  <!-- Header -->
  <div class="row mb-4">
    <div class="col">
      <h3>Contractor Registration Migration</h3>
      <p class="text-muted">
        Import existing contractor registrations from a JSON file. Contractors
        are matched to existing GeoPermits users by username, and registrations
        are linked to existing ContractorTypes by name within the selected
        jurisdiction.
      </p>
      <div class="alert alert-info" role="alert">
        <i class="fa fa-info-circle"></i>
        <strong>Note:</strong> Users must already exist in GeoPermits before
        running the migration. Select the contractor type all registrations
        should be associated with — it will be applied to every contractor
        entry in the file. Custom field TemplateCodes are validated against
        the selected ContractorType's registration workflow; invalid codes
        are skipped with a warning.
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div *ngIf="error" class="row mb-4">
    <div class="col">
      <div class="alert alert-danger alert-dismissible fade show" role="alert">
        <i class="fa fa-exclamation-triangle"></i>
        <strong>Error:</strong> {{ error }}
        <button
          type="button"
          class="close"
          data-dismiss="alert"
          aria-label="Close"
          (click)="error = null"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Migration Form -->
  <div *ngIf="!success" class="row">
    <div class="col-lg-8">
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Migration Details</h5>
        </div>
        <div class="card-body">
          <app-loader *ngIf="loading"></app-loader>

          <form *ngIf="!loading" (ngSubmit)="migrate()">
            <!-- Jurisdiction Selector -->
            <div class="form-group">
              <label class="form-label">
                <strong>Jurisdiction</strong>
                <span class="text-danger">*</span>
              </label>
              <select
                class="form-control"
                [(ngModel)]="selectedClientId"
                name="clientId"
                (ngModelChange)="onClientChange()"
                required
              >
                <option [ngValue]="null">-- Select Jurisdiction --</option>
                <option *ngFor="let client of clients" [value]="client.id">
                  {{ client.name }}
                </option>
              </select>
              <small class="form-text text-muted">
                Select the jurisdiction whose contractor types should be used.
              </small>
            </div>

            <!-- Contractor Type Selector -->
            <div class="form-group">
              <label class="form-label">
                <strong>Contractor Type</strong>
                <span class="text-danger">*</span>
              </label>
              <span *ngIf="loadingContractorTypes" class="ml-2">
                <i class="fa fa-spinner fa-spin"></i>
              </span>
              <select
                class="form-control"
                [(ngModel)]="selectedContractorTypeId"
                name="contractorTypeId"
                [disabled]="!selectedClientId || loadingContractorTypes"
                required
              >
                <option [ngValue]="null">
                  {{ selectedClientId ? '-- Select Contractor Type --' : '-- Select a jurisdiction first --' }}
                </option>
                <option *ngFor="let ct of contractorTypes" [value]="ct.id">
                  {{ ct.name }}
                </option>
              </select>
              <small class="form-text text-muted">
                All registrations in the file will be created under this contractor type.
              </small>
            </div>

            <!-- Migration File Upload -->
            <div class="form-group">
              <label class="form-label">
                <strong>Contractor Registration Migration File (JSON)</strong>
                <span class="text-danger">*</span>
              </label>
              <wm-upload
                #migrationUploadComponent
                buttonText="Select Migration File"
                validDocumentTypes="json"
                [pathSegments]="['contractor-registration-migration', uploadKey]"
                (fileUploaded)="onFileUploaded($event)"
                (fileDeleted)="onFileDeleted($event)"
                [key]="uploadKey + '-contractor-reg'"
                [allowMultipleFiles]="false"
                category="ContractorRegistrationMigration"
                [deleteStoredDocument]="false"
                [showUploadedFilesList]="true"
              ></wm-upload>
              <small class="form-text text-muted">
                Upload the contractor registration migration JSON file. See the
                sample file below for the expected structure.
              </small>
              <div class="mt-2">
                <a
                  href="assets/sample-contractor-registration-migration.json"
                  download="sample-contractor-registration-migration.json"
                  class="btn btn-sm btn-outline-secondary"
                >
                  <i class="fa fa-download"></i> Download Sample File
                </a>
              </div>
            </div>

            <!-- Submit -->
            <div class="form-group mt-3">
              <button
                type="submit"
                class="btn btn-primary"
                [disabled]="!migrationFileUploaded || !selectedClientId || !selectedContractorTypeId"
              >
                <i class="fa fa-upload"></i> Run Migration
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <!-- Results -->
  <div *ngIf="success && migrationResult" class="row mt-4">
    <div class="col-lg-8">
      <div class="card border-success">
        <div class="card-header bg-success text-white">
          <h5 class="mb-0">
            <i class="fa fa-check-circle"></i> Migration Complete
          </h5>
        </div>
        <div class="card-body">
          <!-- Summary Counts -->
          <div class="row text-center mb-3">
            <div class="col-sm-3">
              <div class="card bg-light">
                <div class="card-body py-2">
                  <h4 class="mb-0 text-success">{{ migrationResult.contractorsCreated }}</h4>
                  <small class="text-muted">Contractors Created</small>
                </div>
              </div>
            </div>
            <div class="col-sm-3">
              <div class="card bg-light">
                <div class="card-body py-2">
                  <h4 class="mb-0 text-info">{{ migrationResult.contractorsUpdated }}</h4>
                  <small class="text-muted">Contractors Updated</small>
                </div>
              </div>
            </div>
            <div class="col-sm-3">
              <div class="card bg-light">
                <div class="card-body py-2">
                  <h4 class="mb-0 text-success">{{ migrationResult.registrationsCreated }}</h4>
                  <small class="text-muted">Registrations Created</small>
                </div>
              </div>
            </div>
            <div class="col-sm-3">
              <div class="card bg-light">
                <div class="card-body py-2">
                  <h4 class="mb-0 text-warning">{{ migrationResult.registrationsSkipped }}</h4>
                  <small class="text-muted">Registrations Skipped</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Warnings -->
          <div
            *ngIf="migrationResult.warnings && migrationResult.warnings.length > 0"
            class="alert alert-warning"
          >
            <strong>
              <i class="fa fa-exclamation-triangle"></i>
              {{ migrationResult.warnings.length }} Warning(s):
            </strong>
            <ul class="mb-0 mt-2">
              <li *ngFor="let warning of migrationResult.warnings">{{ warning }}</li>
            </ul>
          </div>

          <!-- Contractor IDs table -->
          <div
            *ngIf="migrationResult.contractorIds && (migrationResult.contractorIds | keyvalue).length > 0"
            class="mt-3"
          >
            <h6>Processed Contractors</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="thead-light">
                  <tr>
                    <th>Username</th>
                    <th>Contractor ID</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let entry of migrationResult.contractorIds | keyvalue">
                    <td>{{ entry.key }}</td>
                    <td><code>{{ entry.value }}</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Run another migration -->
          <div class="mt-3">
            <button
              type="button"
              class="btn btn-secondary"
              (click)="success = false; migrationResult = null; selectedContractorTypeId = null"
            >
              <i class="fa fa-redo"></i> Run Another Migration
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Migration History -->
  <div *ngIf="selectedClientId" class="row mt-4">
    <div class="col-lg-10">
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Migration History</h5>
          <button type="button" class="btn btn-sm btn-outline-secondary" (click)="loadHistory()">
            <i class="fa fa-sync"></i> Refresh
          </button>
        </div>
        <div class="card-body p-0">
          <app-loader *ngIf="loadingHistory"></app-loader>
          <div *ngIf="!loadingHistory && migrationHistory.length === 0" class="p-3 text-muted">
            No migration runs found for this jurisdiction.
          </div>
          <div *ngIf="!loadingHistory && migrationHistory.length > 0" class="table-responsive">
            <table class="table table-sm table-bordered mb-0">
              <thead class="thead-light">
                <tr>
                  <th>Date</th>
                  <th>Contractor Type</th>
                  <th>Status</th>
                  <th class="text-center">Created</th>
                  <th class="text-center">Updated</th>
                  <th class="text-center">Reg Created</th>
                  <th class="text-center">Reg Skipped</th>
                  <th>Run By</th>
                  <th>Warnings</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let r of migrationHistory">
                  <td><small>{{ r.createdOn | date:'short' }}</small></td>
                  <td>{{ r.contractorTypeName ?? '—' }}</td>
                  <td>
                    <span class="badge"
                      [class.badge-secondary]="r.status === 'Pending'"
                      [class.badge-warning]="r.status === 'Running'"
                      [class.badge-success]="r.status === 'Completed'"
                      [class.badge-danger]="r.status === 'Error'"
                    >{{ r.status }}</span>
                    <small *ngIf="r.status === 'Error'" class="text-danger d-block">{{ r.errorMessage }}</small>
                  </td>
                  <td class="text-center">{{ r.contractorsCreated ?? '—' }}</td>
                  <td class="text-center">{{ r.contractorsUpdated ?? '—' }}</td>
                  <td class="text-center">{{ r.registrationsCreated ?? '—' }}</td>
                  <td class="text-center">{{ r.registrationsSkipped ?? '—' }}</td>
                  <td><small>{{ r.migratedBy ?? r.createdBy }}</small></td>
                  <td>
                    <span *ngIf="getWarnings(r).length === 0" class="text-muted">—</span>
                    <span *ngIf="getWarnings(r).length > 0"
                      class="text-warning"
                      [title]="getWarnings(r).join('\n')"
                      style="cursor:pointer">
                      <i class="fa fa-exclamation-triangle"></i> {{ getWarnings(r).length }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>

</div>
